/**
 * Saves one face as a transcript speaker's (PLAT-11992), from fresh server
 * state: the override file is replaced whole, so it must be built from the
 * latest identifications, not a copy the modal opened with.
 */

import {
  fetchFaceDetectionsApi,
  saveSpeakerIdentificationsOverrideApi,
} from '@src/hooks/useFaceDetections';
import { err, ok, type Result } from '@src/services/common/Result';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { fetchSpeakerVoiceMappings } from '@src/utility/speakerUtils';

import { assignFace } from './sceneFaceAssignment';

const logger = LogManager.getLogger('service:mapFaceToSpeaker');

/**
 * @returns the story's rebuilt transcript speaker → face labels map.
 */
export async function mapFaceToSpeaker({
  storyId,
  faceLabel,
  speakerLabel,
  datoSpeakerId: knownDatoSpeakerId,
}: {
  storyId: string;
  faceLabel: string;
  /** Null unassigns the face. */
  speakerLabel: string | null;
  /**
   * The speaker's Dato id when it was just linked — a fresh read of the
   * voice mappings can still miss it while the CMS's CDN catches up.
   */
  datoSpeakerId?: string;
}): Promise<Result<Record<string, string[]>>> {
  try {
    const [faces, voice] = await Promise.all([
      fetchFaceDetectionsApi(storyId),
      fetchSpeakerVoiceMappings(storyId),
    ]);
    const voiceMappings = { ...voice?.mappings };
    if (speakerLabel && knownDatoSpeakerId) {
      voiceMappings[speakerLabel] = knownDatoSpeakerId;
    }
    const datoSpeakerId = speakerLabel ? voiceMappings[speakerLabel] : null;
    if (!faces || (speakerLabel && !datoSpeakerId)) {
      return err(
        'PRECONDITION',
        'Needs face data and a Dato speaker for the transcript speaker',
      );
    }

    const next = assignFace({
      identifications: faces.speakerIdentifications ?? {},
      voiceMappings,
      faceTracks: faces.faceTracks ?? {},
      faceLabel,
      datoSpeakerId: datoSpeakerId ?? null,
    });
    const saved = await saveSpeakerIdentificationsOverrideApi(
      storyId,
      next.identifications,
    );
    if (!saved) return err('EXTERNAL', 'Could not save the face mapping');

    logger.info('Mapped a face to a speaker', { faceLabel, speakerLabel });
    return ok(next.speakerMap);
  } catch (error) {
    logger.warn('Failed to map a face to a speaker', extractErrorInfo(error));
    return err('EXTERNAL', 'Failed to map the face', error);
  }
}
